// apps/web/src/lib/env.ts
//
// Typed runtime env read from Vite's import.meta.env. Vite exposes
// VITE_-prefixed vars to client code at BUILD time; the value is baked
// into the shipped bundle. The Cloudflare preset for TanStack Start
// also forwards Worker bindings during SSR but those are server-only.
//
// LAT-492 testing-gap fix: previous version defaulted `WORKER_BASE_URL`
// to `https://mcp-dev.atmina.ai` when `VITE_WORKER_BASE_URL` was unset,
// which meant a prod build that forgot the env var would silently bake
// the DEV MCP URL into the prod bundle and CORS-fail every call from
// `app.atmina.ai`. The module now throws at load time when running in
// a production build without an explicit value, so the bug can't recur.
//
// Stage-aware build scripts (`build:web:dev` / `build:web:prod` in the
// root package.json) set `VITE_WORKER_BASE_URL` explicitly per stage.
// `bun run build:web` (no stage suffix) is intentionally left as the
// dev-default convenience for local iteration.

const VITE_ENV = (import.meta as unknown as { env: Record<string, string> }).env;
const VITE_WORKER_BASE_URL = VITE_ENV.VITE_WORKER_BASE_URL;

if (!VITE_WORKER_BASE_URL && VITE_ENV.PROD) {
  throw new Error(
    "apps/web prod build is missing VITE_WORKER_BASE_URL. Use " +
      "`bun run build:web:prod` (which sets it to https://mcp.atmina.ai) " +
      "or pass the env var explicitly. The empty-default fallback is dev-only.",
  );
}

export const env = {
  WORKER_BASE_URL: VITE_WORKER_BASE_URL ?? "https://mcp-dev.atmina.ai",
  STAGE: VITE_ENV.VITE_STAGE ?? "dev",
  // LAT-1136 (Getting-Started S6) — the marketing site's origin, where the
  // public release-notes feed and page live (prod → atmina.ai, dev →
  // dev.atmina.ai, matching sst.config.ts's siteCustomDomain). Overridable via
  // VITE_SITE_BASE_URL for local iteration. Unlike WORKER_BASE_URL this is a
  // fully public, cookie-less origin, so a bad value degrades to a failed feed
  // fetch (the popover fails soft), not a credential leak — no prod-build guard.
  SITE_BASE_URL:
    VITE_ENV.VITE_SITE_BASE_URL ??
    (VITE_ENV.VITE_STAGE === "prod"
      ? "https://atmina.ai"
      : "https://dev.atmina.ai"),
  // LAT-822 (C2) — chat-with-documents PoC UI gate. Fail-closed: the chat tab
  // is hidden unless VITE_CHAT_ENABLED === "true" (case-insensitive) at build
  // time. The backend independently fail-closes on CHAT_ENABLED, so both sides
  // must be flipped to expose the feature. Default OFF (not set per stage).
  CHAT_ENABLED: (VITE_ENV.VITE_CHAT_ENABLED ?? "").toLowerCase() === "true",
  // LAT-851 — in-app support widget UI gate. Fail-closed and dark by default:
  // the support button is hidden unless VITE_SUPPORT_ENABLED === "true"
  // (case-insensitive) at build time. Mirrors CHAT_ENABLED. Not set per stage,
  // so the widget ships dark until explicitly flipped.
  SUPPORT_ENABLED: (VITE_ENV.VITE_SUPPORT_ENABLED ?? "").toLowerCase() === "true",
  // LAT-1278 (ADR 0041 §D10) — page_view beacon UI gate. Fail-closed: the
  // TelemetryBeacon mounts (subscribes to navigation + POSTs page views) only
  // when VITE_TELEMETRY_ENABLED === "true" at build time. Mirrors the server's
  // TELEMETRY_ENABLED kill switch; both sides must be on to record. Wired on
  // per stage via build:web:dev / build:web:prod (kill-switch posture) — unset
  // in the no-stage `build:web` convenience so local iteration stays dark.
  TELEMETRY_ENABLED: (VITE_ENV.VITE_TELEMETRY_ENABLED ?? "").toLowerCase() === "true",
} as const;

export const apiBase = `${env.WORKER_BASE_URL}/api/v1`;
export const authBase = `${env.WORKER_BASE_URL}/api/auth`;
